import React from "react"
import {Prism as SyntaxHighlighter} from "react-syntax-highlighter"
import {dracula} from "react-syntax-highlighter/dist/cjs/styles/prism"

import globalStyles from "../../../../../App.module.scss"
import {Arrow, Box, Diagram, DiagramCaption} from "../../../Architecture/shared/Diagram"

const chartLayoutExample = `mychart/
├── Chart.yaml          # name, version (of the chart), appVersion (of the app), dependencies
├── values.yaml         # default values — everything a user can override
├── charts/             # dependency charts, filled in by "helm dependency update"
├── crds/               # CRDs, installed once before anything else (never upgraded)
└── templates/
    ├── _helpers.tpl    # named templates (fullname, labels) — not rendered on their own
    ├── deployment.yaml
    ├── service.yaml
    └── NOTES.txt       # printed after install/upgrade`

const templateExample = `# templates/deployment.yaml
apiVersion: apps/v1
kind: Deployment
metadata:
  name: {{ include "mychart.fullname" . }}
  labels:
    {{- include "mychart.labels" . | nindent 4 }}
spec:
  replicas: {{ .Values.replicaCount }}
  selector:
    matchLabels:
      {{- include "mychart.selectorLabels" . | nindent 6 }}
  template:
    metadata:
      labels:
        {{- include "mychart.selectorLabels" . | nindent 8 }}
    spec:
      containers:
        - name: {{ .Chart.Name }}
          image: "{{ .Values.image.repository }}:{{ .Values.image.tag | default .Chart.AppVersion }}"
          ports:
            - containerPort: {{ .Values.service.targetPort }}
          {{- with .Values.env }}
          env:
            {{- toYaml . | nindent 12 }}
          {{- end }}
          resources:
            {{- toYaml .Values.resources | nindent 12 }}`

const valuesExample = `# values.yaml
replicaCount: 2

image:
  repository: ghcr.io/me/hello-web
  tag: ""            # empty = fall back to Chart.yaml appVersion

service:
  port: 80
  targetPort: 8080

env: []
#  - name: LOG_LEVEL
#    value: debug

resources:
  requests:
    cpu: 100m
    memory: 128Mi
  limits:
    memory: 256Mi`

const workflowExample = `# 1. Find the chart and see what it lets you configure
helm repo add podinfo https://stefanprodan.github.io/podinfo
helm repo update
helm search repo podinfo --versions | head
helm show values podinfo/podinfo > values-podinfo.yaml

# 2. Edit only what you need, then preview the rendered YAML
helm template my-app podinfo/podinfo -f values-podinfo.yaml | less

# 3. Install or upgrade — the same command works for both
helm upgrade --install my-app podinfo/podinfo \\
  --namespace demo --create-namespace \\
  --version 6.9.0 \\
  -f values-podinfo.yaml \\
  --wait --timeout 5m \\
  --rollback-on-failure        # Helm 3: --atomic

# 4. Check it, and roll back if needed
helm status my-app -n demo
helm history my-app -n demo
helm rollback my-app 1 -n demo`

const ociExample = `# Charts can live in any OCI registry (GHCR, ECR, Harbor, Docker Hub…) — no "helm repo add" needed
helm registry login ghcr.io -u my-user --password-stdin < token.txt

helm package ./mychart                          # -> mychart-0.1.0.tgz
helm push mychart-0.1.0.tgz oci://ghcr.io/my-user/charts

helm install my-app oci://ghcr.io/my-user/charts/mychart --version 0.1.0
helm show values oci://ghcr.io/stefanprodan/charts/podinfo`

export default function HelmCheatSheet() {
    return (
        <div className={globalStyles.article}>
            <h1>Helm in a Nutshell</h1>

            <p>Helm is the package manager for Kubernetes. A <b>chart</b> bundles a set of templated
                manifests together with sensible defaults; you install it with your own values and Helm
                renders the final YAML, applies it, and remembers what it did — so you can upgrade, inspect
                and roll back the whole application as one unit instead of juggling a dozen loose
                files.</p>

            <h3>The core idea: templates + values = release</h3>
            <p>A chart never changes per environment — only the <b>values</b> you feed it do. Every
                install or upgrade produces a numbered <b>revision</b> of a named <b>release</b>, stored in
                the cluster itself, which is what makes <code>helm rollback</code> possible.</p>

            <h3>What happens on helm upgrade --install</h3>
            <Diagram viewBox="0 0 900 230">
                <Box x={20} y={20} width={150} height={44} label="Chart" sublabel="templates + defaults"/>
                <Box x={20} y={150} width={150} height={44} label="Your values" sublabel="-f file / --set"/>
                <Box x={260} y={85} width={150} height={44} label="helm" sublabel="renders templates" accent/>
                <Box x={500} y={85} width={150} height={44} label="Kubernetes API" sublabel="applies manifests"/>
                <Box x={730} y={20} width={150} height={44} label="Release secret" sublabel="one per revision"/>
                <Box x={730} y={150} width={150} height={44} label="Your resources" sublabel="Deployment, Service…"/>

                <Arrow x1={170} y1={42} x2={260} y2={98}/>
                <Arrow x1={170} y1={172} x2={260} y2={116}/>
                <Arrow x1={410} y1={107} x2={500} y2={107} label="manifests"/>
                <Arrow x1={650} y1={98} x2={730} y2={42} dashed/>
                <Arrow x1={650} y1={116} x2={730} y2={172}/>
            </Diagram>
            <DiagramCaption>Helm renders everything client-side, sends plain manifests to the API server,
                and records the release (chart, values and rendered output) as a Secret named{' '}
                <code>sh.helm.release.v1.&lt;name&gt;.v&lt;revision&gt;</code> in the release's namespace.
                There is no Helm server component in the cluster.
            </DiagramCaption>

            <h3>Vocabulary</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th>Term</th>
                        <th>What it is</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><b>Chart</b></td>
                        <td>A directory (or <code>.tgz</code>) of templates, default values and metadata</td>
                    </tr>
                    <tr>
                        <td><b>Values</b></td>
                        <td>The YAML tree the templates read from — <code>values.yaml</code> defaults, overridden
                            by your files and flags
                        </td>
                    </tr>
                    <tr>
                        <td><b>Release</b></td>
                        <td>One installed instance of a chart, named and scoped to a namespace — you can install
                            the same chart many times under different names
                        </td>
                    </tr>
                    <tr>
                        <td><b>Revision</b></td>
                        <td>A numbered snapshot of a release, created on every install, upgrade and rollback</td>
                    </tr>
                    <tr>
                        <td><b>Repository</b></td>
                        <td>An HTTP server with an <code>index.yaml</code> listing charts — added with{' '}
                            <code>helm repo add</code>
                        </td>
                    </tr>
                    <tr>
                        <td><b>OCI registry</b></td>
                        <td>Charts stored as OCI artifacts in a container registry, referenced as{' '}
                            <code>oci://…</code> — the modern alternative to classic repositories
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Values precedence</h3>
            <p>When the same key is set in several places, the last one wins, in this order:</p>
            <ol>
                <li>The chart's own <code>values.yaml</code></li>
                <li>A parent chart's values, for a subchart (under the subchart's name)</li>
                <li>Each <code>-f / --values</code> file, left to right</li>
                <li><code>--set</code>, <code>--set-string</code> and <code>--set-file</code> flags</li>
            </ol>
            <p>A common pattern is one shared file plus one per environment:{' '}
                <code>-f values.yaml -f values-prod.yaml</code>.</p>

            <h3>A typical workflow with a public chart</h3>
            <SyntaxHighlighter
                children={workflowExample}
                style={dracula}
                PreTag="div"
                language={'bash'}
            />

            <h3>Helm cheat sheet</h3>
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Repositories &amp; finding charts</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>helm repo add podinfo https://stefanprodan.github.io/podinfo</code></td>
                        <td>Register a chart repository under a local alias</td>
                    </tr>
                    <tr>
                        <td><code>helm repo update</code></td>
                        <td>Refresh every repo's index — do this before searching or upgrading</td>
                    </tr>
                    <tr>
                        <td><code>helm repo list</code></td>
                        <td>Show configured repositories</td>
                    </tr>
                    <tr>
                        <td><code>helm search repo podinfo --versions</code></td>
                        <td>Search your added repos, listing every available chart version</td>
                    </tr>
                    <tr>
                        <td><code>helm search hub cert-manager</code></td>
                        <td>Search Artifact Hub for charts you haven't added yet</td>
                    </tr>
                    <tr>
                        <td><code>helm show values podinfo/podinfo</code></td>
                        <td>Print the chart's default values — the real documentation for most charts</td>
                    </tr>
                    <tr>
                        <td><code>helm pull podinfo/podinfo --untar</code></td>
                        <td>Download and unpack a chart locally to read its templates</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Install &amp; upgrade</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>helm install my-app podinfo/podinfo</code></td>
                        <td>Install a new release; fails if the name already exists</td>
                    </tr>
                    <tr>
                        <td><code>helm upgrade --install my-app podinfo/podinfo -f values.yaml</code></td>
                        <td>Install if missing, upgrade if present — the idempotent form to use in CI</td>
                    </tr>
                    <tr>
                        <td><code>-n demo --create-namespace</code></td>
                        <td>Target a namespace, creating it if needed — every command takes <code>-n</code></td>
                    </tr>
                    <tr>
                        <td><code>--version 6.9.0</code></td>
                        <td>Pin the chart version; without it you get whatever is latest today</td>
                    </tr>
                    <tr>
                        <td><code>--set replicaCount=3 --set ui.message=hi</code></td>
                        <td>Override single values inline; dots walk into nested keys</td>
                    </tr>
                    <tr>
                        <td><code>--set-string image.tag=1.10</code></td>
                        <td>Force a string — plain <code>--set</code> would turn <code>1.10</code> into the
                            number <code>1.1</code>
                        </td>
                    </tr>
                    <tr>
                        <td><code>--wait --timeout 5m</code></td>
                        <td>Only report success once Pods are ready, not just once the API accepted the YAML
                        </td>
                    </tr>
                    <tr>
                        <td><code>--rollback-on-failure</code></td>
                        <td>Roll back automatically if the upgrade fails (implies <code>--wait</code>);
                            called <code>--atomic</code> in Helm 3
                        </td>
                    </tr>
                    <tr>
                        <td><code>--dry-run=server --debug</code></td>
                        <td>Render and validate against the live API without changing anything</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Inspecting releases</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>helm list -A</code></td>
                        <td>Every release in every namespace; add <code>--all</code> to include failed and
                            pending ones
                        </td>
                    </tr>
                    <tr>
                        <td><code>helm status my-app -n demo</code></td>
                        <td>Current state, last deploy time and the chart's NOTES</td>
                    </tr>
                    <tr>
                        <td><code>helm history my-app -n demo</code></td>
                        <td>Every revision with its status, chart version and description</td>
                    </tr>
                    <tr>
                        <td><code>helm get values my-app -n demo</code></td>
                        <td>Only the values <i>you</i> supplied; add <code>--all</code> for the fully merged set
                        </td>
                    </tr>
                    <tr>
                        <td><code>helm get manifest my-app -n demo</code></td>
                        <td>The exact YAML Helm applied for the current revision</td>
                    </tr>
                    <tr>
                        <td><code>helm get values my-app --revision 2</code></td>
                        <td>What the values were at an older revision — handy before a rollback</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Rollback &amp; removal</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>helm rollback my-app -n demo</code></td>
                        <td>Go back to the previous revision (creates a new revision, it doesn't rewrite
                            history)
                        </td>
                    </tr>
                    <tr>
                        <td><code>helm rollback my-app 3 -n demo</code></td>
                        <td>Go back to a specific revision from <code>helm history</code></td>
                    </tr>
                    <tr>
                        <td><code>helm uninstall my-app -n demo</code></td>
                        <td>Delete every resource in the release plus its history</td>
                    </tr>
                    <tr>
                        <td><code>helm uninstall my-app --keep-history</code></td>
                        <td>Delete the resources but keep the history, so it can still be rolled back</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Rendering &amp; diffing</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>helm template my-app ./mychart -f values.yaml</code></td>
                        <td>Render to stdout without touching a cluster — pipe into{' '}
                            <code>kubectl apply</code>, a linter or a GitOps repo
                        </td>
                    </tr>
                    <tr>
                        <td><code>helm template … -s templates/deployment.yaml</code></td>
                        <td>Render a single template while debugging it</td>
                    </tr>
                    <tr>
                        <td><code>helm diff upgrade my-app podinfo/podinfo -f values.yaml</code></td>
                        <td>Show what an upgrade would change, <code>kubectl diff</code>-style (needs the{' '}
                            <code>helm-diff</code> plugin)
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th colSpan="2">Writing your own chart</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>helm create mychart</code></td>
                        <td>Scaffold a working chart (Deployment, Service, Ingress, HPA, helpers)</td>
                    </tr>
                    <tr>
                        <td><code>helm lint ./mychart</code></td>
                        <td>Catch template and structure errors before installing</td>
                    </tr>
                    <tr>
                        <td><code>helm dependency update ./mychart</code></td>
                        <td>Download the dependencies listed in <code>Chart.yaml</code> into{' '}
                            <code>charts/</code> and write <code>Chart.lock</code>
                        </td>
                    </tr>
                    <tr>
                        <td><code>helm dependency build ./mychart</code></td>
                        <td>Rebuild <code>charts/</code> from the existing <code>Chart.lock</code> — use this in
                            CI
                        </td>
                    </tr>
                    <tr>
                        <td><code>helm package ./mychart</code></td>
                        <td>Produce a versioned <code>mychart-0.1.0.tgz</code> ready to publish</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Anatomy of a chart</h3>
            <SyntaxHighlighter
                children={chartLayoutExample}
                style={dracula}
                PreTag="div"
                language={'text'}
            />

            <h3>A template and its values</h3>
            <p>Templates are Go templates with the Sprig function library. The objects you'll use most are{' '}
                <code>.Values</code>, <code>.Release</code> (<code>.Name</code>, <code>.Namespace</code>),{' '}
                <code>.Chart</code> and the helpers defined in <code>_helpers.tpl</code>.</p>
            <SyntaxHighlighter
                children={templateExample}
                style={dracula}
                PreTag="div"
                language={'yaml'}
            />
            <SyntaxHighlighter
                children={valuesExample}
                style={dracula}
                PreTag="div"
                language={'yaml'}
            />
            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '680px'}}>
                    <thead>
                    <tr>
                        <th>Template idiom</th>
                        <th>What it does</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>{'{{- … }}'}</code> / <code>{'{{ … -}}'}</code></td>
                        <td>Trim whitespace and newlines before / after the tag — the usual cause of broken
                            indentation
                        </td>
                    </tr>
                    <tr>
                        <td><code>{'toYaml .Values.resources | nindent 12'}</code></td>
                        <td>Dump a whole values subtree as YAML, indented to fit where it lands</td>
                    </tr>
                    <tr>
                        <td><code>{'{{- with .Values.env }} … {{- end }}'}</code></td>
                        <td>Render a block only if the value is set, with <code>.</code> scoped to it</td>
                    </tr>
                    <tr>
                        <td><code>{'{{ .Values.tag | default .Chart.AppVersion }}'}</code></td>
                        <td>Fallback when a value is empty</td>
                    </tr>
                    <tr>
                        <td><code>{'{{ required "image.repository is required" .Values.image.repository }}'}</code>
                        </td>
                        <td>Fail rendering with a clear message instead of producing broken YAML</td>
                    </tr>
                    <tr>
                        <td><code>{'{{ include "mychart.fullname" . }}'}</code></td>
                        <td>Call a named template from <code>_helpers.tpl</code> and use its output as a
                            string
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>OCI registries</h3>
            <SyntaxHighlighter
                children={ociExample}
                style={dracula}
                PreTag="div"
                language={'bash'}
            />

            <h3>Things that trip people up</h3>
            <ul>
                <li><b>Forgetting <code>-n</code></b> — releases are scoped to a namespace, so{' '}
                    <code>helm list</code> or <code>helm upgrade</code> in the wrong one simply can't see the
                    release (and <code>upgrade --install</code> will happily create a second copy).
                </li>
                <li><b><code>--reuse-values</code> on upgrade</b> keeps your old values but also ignores new
                    defaults from the newer chart version. Passing the same <code>-f</code> files every time
                    is more predictable.
                </li>
                <li><b>"another operation (install/upgrade/rollback) is in progress"</b> means a previous run
                    was interrupted and left the release <code>pending-*</code>. Check{' '}
                    <code>helm history</code> and <code>helm rollback</code> to the last good revision.
                </li>
                <li><b>CRDs in <code>crds/</code> are installed once and never upgraded</b> or deleted by Helm —
                    charts that need CRD upgrades usually ship them as regular templates or document a manual
                    step.
                </li>
                <li><b><code>helm uninstall</code> leaves PVCs behind</b> when they were created from a
                    StatefulSet's <code>volumeClaimTemplates</code> — the data survives until you delete them
                    yourself.
                </li>
                <li><b>Editing resources with <code>kubectl</code></b> after a Helm install creates drift that
                    the next <code>helm upgrade</code> may silently overwrite. Change the values instead.
                </li>
                <li><b>Helm 4 renamed flags</b> — <code>--atomic</code> became{' '}
                    <code>--rollback-on-failure</code> and <code>--force</code> became{' '}
                    <code>--force-replace</code>; the old names still work with a deprecation warning. New
                    installs also use server-side apply by default.
                </li>
            </ul>
        </div>
    )
}
